import { useState, useRef, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import type { BoundingBox, ListingSibling } from "@/types";
import { BboxStatusIcon } from "@/components/listings/BboxStatusIcon";

interface RecognizedGameBox {
  name: string;
  boundingBox: BoundingBox;
}

interface HighlightModeProps {
  mode: "highlight";
  games: RecognizedGameBox[];
  imageUrl: string;
}

interface DetailModeProps {
  mode: "detail";
  activeBoundingBox: BoundingBox;
  siblings: ListingSibling[];
  imageUrl: string;
}

interface EditModeProps {
  mode: "edit";
  activeBoundingBox: BoundingBox | null;
  siblings: ListingSibling[];
  imageUrl: string;
  newBox?: BoundingBox | null;
  readOnlySiblings?: boolean;
  hideDeleteButton?: boolean;
  onAddBox: (box: BoundingBox) => void;
  onUpdateNewBox?: (box: BoundingBox) => void;
  onRemoveSibling: (siblingId: string) => void;
  onRemoveCurrentBox: () => void;
  onUpdateActiveBox: (box: BoundingBox) => void;
  onUpdateSiblingBox: (siblingId: string, box: BoundingBox) => void;
}

type BoundingBoxOverlayProps = HighlightModeProps | DetailModeProps | EditModeProps;

const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#8b5cf6", "#ec4899", "#06b6d4"];

export function BoundingBoxOverlay(props: BoundingBoxOverlayProps) {
  const { mode, imageUrl } = props;
  const [aspectRatio, setAspectRatio] = useState(1);

  const onImageLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    if (naturalWidth > 0) setAspectRatio(naturalHeight / naturalWidth);
  }, []);

  return (
    <div className="relative w-full">
      <img
        src={imageUrl}
        alt=""
        className="w-full rounded-xl"
        onLoad={onImageLoad}
      />
      {mode === "edit" ? (
        <EditOverlay
          activeBoundingBox={props.activeBoundingBox}
          siblings={props.siblings}
          newBox={props.newBox}
          readOnlySiblings={props.readOnlySiblings}
          hideDeleteButton={props.hideDeleteButton}
          onAddBox={props.onAddBox}
          onUpdateNewBox={props.onUpdateNewBox}
          onRemoveSibling={props.onRemoveSibling}
          onRemoveCurrentBox={props.onRemoveCurrentBox}
          onUpdateActiveBox={props.onUpdateActiveBox}
          onUpdateSiblingBox={props.onUpdateSiblingBox}
        />
      ) : (
        <svg
          viewBox="0 0 1000 1000"
          className="pointer-events-none absolute inset-0 h-full w-full"
          preserveAspectRatio="none"
        >
          {mode === "highlight" ? (
            <HighlightBoxes games={props.games} />
          ) : (
            <DetailBoxes
              activeBoundingBox={props.activeBoundingBox}
              siblings={props.siblings}
              aspectRatio={aspectRatio}
            />
          )}
        </svg>
      )}
    </div>
  );
}

function HighlightBoxes({ games }: { games: RecognizedGameBox[] }) {
  return (
    <>
      {games.map((game, i) => {
        const color = COLORS[i % COLORS.length];
        const { yMin, xMin, yMax, xMax } = game.boundingBox;
        const width = xMax - xMin;
        const height = yMax - yMin;

        return (
          <g key={i}>
            <rect
              x={xMin}
              y={yMin}
              width={width}
              height={height}
              fill={color}
              fillOpacity={0.1}
              stroke={color}
              strokeWidth={3}
              rx={8}
            />
            <rect
              x={xMin}
              y={Math.max(0, yMin - 32)}
              width={Math.min(width, game.name.length * 10 + 80)}
              height={28}
              fill={color}
              rx={4}
            />
            <text
              x={xMin + 6}
              y={Math.max(0, yMin - 32) + 19}
              fill="white"
              fontSize={14}
              fontWeight="bold"
            >
              {game.name}
            </text>
          </g>
        );
      })}
    </>
  );
}

function DetailBoxes({
  activeBoundingBox,
  siblings,
  aspectRatio,
}: {
  activeBoundingBox: BoundingBox;
  siblings: ListingSibling[];
  aspectRatio: number;
}) {
  const navigate = useNavigate();
  const [hoveredSiblingId, setHoveredSiblingId] = useState<string | null>(null);
  const clearHoverTimeout = useRef<number | null>(null);

  const requestHover = useCallback((id: string | null) => {
    if (clearHoverTimeout.current != null) {
      window.clearTimeout(clearHoverTimeout.current);
      clearHoverTimeout.current = null;
    }
    if (id == null) {
      clearHoverTimeout.current = window.setTimeout(() => {
        setHoveredSiblingId(null);
        clearHoverTimeout.current = null;
      }, 120);
    } else {
      setHoveredSiblingId(id);
    }
  }, []);

  useEffect(() => () => {
    if (clearHoverTimeout.current != null) {
      window.clearTimeout(clearHoverTimeout.current);
    }
  }, []);

  const hoveredSibling = hoveredSiblingId
    ? siblings.find((s) => s.id === hoveredSiblingId)
    : null;
  const focusedBox = hoveredSibling?.boundingBox ?? activeBoundingBox;
  const { yMin, xMin, yMax, xMax } = focusedBox;

  const getColor = (index: number, status: string) => {
    if (status === "cancelled") return "#9ca3af";
    if (status === "sold") return "#ef4444";
    if (status === "reserved") return "#f59e0b";
    return COLORS[index % COLORS.length];
  };

  const activeColor = getColor(0, "active");

  return (
    <>
      {/* Dim area outside focused bounding box */}
      <defs>
        <mask id="active-bbox-mask">
          <rect x="0" y="0" width="1000" height="1000" fill="white" />
          <rect
            x={xMin}
            y={yMin}
            width={xMax - xMin}
            height={yMax - yMin}
            fill="black"
            rx={8}
            style={{ transition: "x 180ms ease, y 180ms ease, width 180ms ease, height 180ms ease" }}
          />
        </mask>
      </defs>
      <rect
        x="0"
        y="0"
        width="1000"
        height="1000"
        fill="black"
        fillOpacity={0.35}
        mask="url(#active-bbox-mask)"
      />

      {/* Active listing box */}
      <rect
        x={activeBoundingBox.xMin}
        y={activeBoundingBox.yMin}
        width={activeBoundingBox.xMax - activeBoundingBox.xMin}
        height={activeBoundingBox.yMax - activeBoundingBox.yMin}
        fill={activeColor}
        fillOpacity={0.08}
        stroke={activeColor}
        strokeWidth={4}
        rx={8}
      />

      {/* Sibling boxes */}
      {siblings.map((sibling, i) => {
        if (!sibling.boundingBox) return null;
        const bb = sibling.boundingBox;
        const isSold = sibling.status === "sold";
        const isReserved = sibling.status === "reserved";
        const isCancelled = sibling.status === "cancelled";
        const isInactive = isSold || isCancelled;
        const isClickable = !isSold && !isCancelled;
        const color = getColor(i + 1, sibling.status);
        const statusLabel = isSold ? " (parduota)" : isReserved ? " (rezervuota)" : isCancelled ? " (atšaukta)" : "";
        const titleText = sibling.gameTitle + statusLabel;
        const bw = bb.xMax - bb.xMin;
        const bh = bb.yMax - bb.yMin;
        const titleWidth = Math.min(bw - 8, titleText.length * 9.5 + 24);
        const showPrice = sibling.price != null && !isCancelled;
        const priceText = showPrice ? `${sibling.price!.toFixed(2)} €` : "";
        const priceFontSize = 26;
        const priceWidth = showPrice ? priceText.length * priceFontSize * 0.62 + 28 : 0;
        const priceTagHeight = 44;

        const isHovered = hoveredSiblingId === sibling.id;

        return (
          <g
            key={sibling.id}
            className="pointer-events-auto"
            style={{ cursor: isClickable ? "pointer" : "default" }}
            onClick={isClickable ? () => navigate(`/listings/${sibling.id}`) : undefined}
            onMouseEnter={() => requestHover(sibling.id)}
            onMouseLeave={() => requestHover(null)}
          >
            <rect
              x={bb.xMin}
              y={bb.yMin}
              width={bw}
              height={bh}
              fill={color}
              fillOpacity={isInactive ? 0.2 : 0.08}
              stroke={color}
              strokeWidth={isHovered ? 5 : isCancelled ? 2 : 3}
              strokeDasharray={isCancelled ? "8 4" : "0"}
              rx={8}
              style={{ transition: "stroke-width 150ms ease" }}
            />
            {(isInactive || isReserved) && (
              <BboxStatusIcon
                cx={bb.xMin + bw / 2}
                cy={bb.yMin + bh / 2}
                status={sibling.status}
                color={color}
                size={Math.min(bw, bh) * 0.4}
                aspectRatio={aspectRatio}
                minRadius={28}
              />
            )}
            {/* Title pill + price tag */}
            {(() => {
              const pillY = bb.yMin + 4;
              return (
                <g>
                  <rect
                    x={bb.xMin + 4}
                    y={pillY}
                    width={titleWidth}
                    height={30}
                    fill={color}
                    fillOpacity={0.9}
                    rx={5}
                  />
                  <text
                    x={bb.xMin + 4 + 8}
                    y={pillY + 20}
                    fill="white"
                    fontSize={14}
                    fontWeight="600"
                  >
                    {titleText}
                  </text>
                  {showPrice && (() => {
                    const priceX = bb.xMax - priceWidth - 4;
                    const priceY = bb.yMin + 4;
                    return (
                      <>
                        <rect x={priceX} y={priceY} width={priceWidth} height={priceTagHeight} fill="white" fillOpacity={0.95} rx={6} />
                        <rect x={priceX} y={priceY} width={priceWidth} height={priceTagHeight} fill={color} fillOpacity={0.1} stroke={color} strokeWidth={2} rx={6} />
                        <text x={priceX + 14} y={priceY + 30} fill={color} fontSize={priceFontSize} fontWeight="800">
                          {priceText}
                        </text>
                      </>
                    );
                  })()}
                </g>
              );
            })()}
          </g>
        );
      })}
    </>
  );
}

function screenToViewBox(svgEl: SVGSVGElement, clientX: number, clientY: number): { x: number; y: number } {
  const ctm = svgEl.getScreenCTM();
  if (!ctm) return { x: 0, y: 0 };
  const inverse = ctm.inverse();
  const pt = svgEl.createSVGPoint();
  pt.x = clientX;
  pt.y = clientY;
  const transformed = pt.matrixTransform(inverse);
  return {
    x: Math.max(0, Math.min(1000, Math.round(transformed.x))),
    y: Math.max(0, Math.min(1000, Math.round(transformed.y))),
  };
}

function isInsideBox(x: number, y: number, box: BoundingBox): boolean {
  return x >= box.xMin && x <= box.xMax && y >= box.yMin && y <= box.yMax;
}

type BoxId = "active" | string;

interface DragState {
  type: "draw" | "move" | "resize";
  startX: number;
  startY: number;
  currentX: number;
  currentY: number;
  boxId?: BoxId;
  handle?: "nw" | "ne" | "sw" | "se";
  originalBox?: BoundingBox;
}

function computeDragBox(d: DragState): BoundingBox | null {
  if (d.type === "draw") {
    return {
      xMin: Math.round(Math.min(d.startX, d.currentX)),
      yMin: Math.round(Math.min(d.startY, d.currentY)),
      xMax: Math.round(Math.max(d.startX, d.currentX)),
      yMax: Math.round(Math.max(d.startY, d.currentY)),
    };
  }

  if (!d.originalBox) return null;
  const dx = d.currentX - d.startX;
  const dy = d.currentY - d.startY;
  const ob = d.originalBox;

  if (d.type === "move") {
    const w = ob.xMax - ob.xMin;
    const h = ob.yMax - ob.yMin;
    let newXMin = ob.xMin + dx;
    let newYMin = ob.yMin + dy;
    newXMin = Math.max(0, Math.min(1000 - w, newXMin));
    newYMin = Math.max(0, Math.min(1000 - h, newYMin));
    return {
      xMin: Math.round(newXMin),
      yMin: Math.round(newYMin),
      xMax: Math.round(newXMin + w),
      yMax: Math.round(newYMin + h),
    };
  }

  if (d.type === "resize" && d.handle) {
    let { xMin, yMin, xMax, yMax } = ob;
    switch (d.handle) {
      case "nw": xMin += dx; yMin += dy; break;
      case "ne": xMax += dx; yMin += dy; break;
      case "sw": xMin += dx; yMax += dy; break;
      case "se": xMax += dx; yMax += dy; break;
    }
    return {
      xMin: Math.max(0, Math.min(1000, Math.round(Math.min(xMin, xMax)))),
      yMin: Math.max(0, Math.min(1000, Math.round(Math.min(yMin, yMax)))),
      xMax: Math.max(0, Math.min(1000, Math.round(Math.max(xMin, xMax)))),
      yMax: Math.max(0, Math.min(1000, Math.round(Math.max(yMin, yMax)))),
    };
  }

  return null;
}

function EditOverlay({
  activeBoundingBox,
  siblings,
  newBox,
  readOnlySiblings,
  hideDeleteButton,
  onAddBox,
  onUpdateNewBox,
  onRemoveSibling,
  onRemoveCurrentBox,
  onUpdateActiveBox,
  onUpdateSiblingBox,
}: {
  activeBoundingBox: BoundingBox | null;
  siblings: ListingSibling[];
  newBox?: BoundingBox | null;
  readOnlySiblings?: boolean;
  hideDeleteButton?: boolean;
  onAddBox: (box: BoundingBox) => void;
  onUpdateNewBox?: (box: BoundingBox) => void;
  onRemoveSibling: (siblingId: string) => void;
  onRemoveCurrentBox: () => void;
  onUpdateActiveBox: (box: BoundingBox) => void;
  onUpdateSiblingBox: (siblingId: string, box: BoundingBox) => void;
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const [selectedId, setSelectedId] = useState<BoxId | null>(null);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [hoverCursor, setHoverCursor] = useState<string>("crosshair");

  const HANDLE_HIT = 25;

  const getOriginalBox = useCallback((id: BoxId): BoundingBox | null => {
    if (id === "active") return activeBoundingBox;
    if (id === "new") return newBox ?? null;
    return siblings.find((s) => s.id === id)?.boundingBox ?? null;
  }, [activeBoundingBox, siblings, newBox]);

  const getDisplayBox = useCallback((id: BoxId): BoundingBox | null => {
    const original = getOriginalBox(id);
    if (!original) return null;
    if (drag && drag.boxId === id) {
      return computeDragBox(drag) ?? original;
    }
    return original;
  }, [getOriginalBox, drag]);

  const handleMouseDown = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const { x, y } = screenToViewBox(svgRef.current, e.clientX, e.clientY);

    // Check resize handles of selected box first
    if (selectedId && !(readOnlySiblings && selectedId !== "active" && selectedId !== "new")) {
      const box = getOriginalBox(selectedId);
      if (box) {
        const handles: Array<{ name: "nw" | "ne" | "sw" | "se"; hx: number; hy: number }> = [
          { name: "nw", hx: box.xMin, hy: box.yMin },
          { name: "ne", hx: box.xMax, hy: box.yMin },
          { name: "sw", hx: box.xMin, hy: box.yMax },
          { name: "se", hx: box.xMax, hy: box.yMax },
        ];
        for (const h of handles) {
          if (Math.abs(x - h.hx) < HANDLE_HIT && Math.abs(y - h.hy) < HANDLE_HIT) {
            setDrag({
              type: "resize", startX: x, startY: y, currentX: x, currentY: y,
              boxId: selectedId, handle: h.name, originalBox: { ...box },
            });
            return;
          }
        }
      }
    }

    // Check if clicking inside any box → select + start move
    if (activeBoundingBox && isInsideBox(x, y, activeBoundingBox)) {
      setSelectedId("active");
      setDrag({
        type: "move", startX: x, startY: y, currentX: x, currentY: y,
        boxId: "active", originalBox: { ...activeBoundingBox },
      });
      return;
    }
    if (!readOnlySiblings) {
      for (const sib of siblings) {
        if (sib.boundingBox && isInsideBox(x, y, sib.boundingBox)) {
          setSelectedId(sib.id);
          setDrag({
            type: "move", startX: x, startY: y, currentX: x, currentY: y,
            boxId: sib.id, originalBox: { ...sib.boundingBox },
          });
          return;
        }
      }
    }
    if (newBox && isInsideBox(x, y, newBox)) {
      setSelectedId("new");
      setDrag({
        type: "move", startX: x, startY: y, currentX: x, currentY: y,
        boxId: "new", originalBox: { ...newBox },
      });
      return;
    }

    // Empty space → deselect and draw new box
    setSelectedId(null);
    setDrag({ type: "draw", startX: x, startY: y, currentX: x, currentY: y });
  }, [selectedId, activeBoundingBox, siblings, newBox, getOriginalBox]);

  const handleMouseMove = useCallback((e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current) return;
    const { x, y } = screenToViewBox(svgRef.current, e.clientX, e.clientY);

    if (drag) {
      setDrag((prev) => prev ? { ...prev, currentX: x, currentY: y } : null);
      return;
    }

    // Update cursor based on what's under the mouse
    if (selectedId && !(readOnlySiblings && selectedId !== "active" && selectedId !== "new")) {
      const box = getOriginalBox(selectedId);
      if (box) {
        const handles: Array<{ name: string; hx: number; hy: number }> = [
          { name: "nw", hx: box.xMin, hy: box.yMin },
          { name: "ne", hx: box.xMax, hy: box.yMin },
          { name: "sw", hx: box.xMin, hy: box.yMax },
          { name: "se", hx: box.xMax, hy: box.yMax },
        ];
        for (const h of handles) {
          if (Math.abs(x - h.hx) < HANDLE_HIT && Math.abs(y - h.hy) < HANDLE_HIT) {
            setHoverCursor(h.name === "nw" || h.name === "se" ? "nwse-resize" : "nesw-resize");
            return;
          }
        }
      }
    }

    // Check if hovering over any box
    if (activeBoundingBox && isInsideBox(x, y, activeBoundingBox)) {
      setHoverCursor("grab");
      return;
    }
    if (!readOnlySiblings) {
      for (const sib of siblings) {
        if (sib.boundingBox && isInsideBox(x, y, sib.boundingBox)) {
          setHoverCursor("grab");
          return;
        }
      }
    }
    if (newBox && isInsideBox(x, y, newBox)) {
      setHoverCursor("grab");
      return;
    }

    setHoverCursor("crosshair");
  }, [drag, selectedId, activeBoundingBox, siblings, newBox, getOriginalBox]);

  const handleMouseUp = useCallback(() => {
    if (!drag) return;
    const box = computeDragBox(drag);
    setDrag(null);
    if (!box) return;

    const width = box.xMax - box.xMin;
    const height = box.yMax - box.yMin;

    if (drag.type === "draw") {
      if (width < 30 || height < 30) return;
      onAddBox(box);
    } else if (drag.type === "move" || drag.type === "resize") {
      if (width < 10 || height < 10) return;
      // Check if actually changed
      const orig = drag.originalBox!;
      if (box.xMin === orig.xMin && box.yMin === orig.yMin && box.xMax === orig.xMax && box.yMax === orig.yMax) {
        return; // Just a click to select, no movement
      }
      if (drag.boxId === "active") {
        onUpdateActiveBox(box);
      } else if (drag.boxId === "new") {
        onUpdateNewBox?.(box);
      } else if (drag.boxId) {
        onUpdateSiblingBox(drag.boxId, box);
      }
    }
  }, [drag, onAddBox, onUpdateActiveBox, onUpdateNewBox, onUpdateSiblingBox]);

  // Drawing rectangle preview
  const drawRect = drag?.type === "draw" ? computeDragBox(drag) : null;

  // Active box display (may be mid-drag)
  const activeDisplay = activeBoundingBox ? getDisplayBox("active") : null;

  const activeCursor = drag
    ? drag.type === "move" ? "grabbing"
      : drag.type === "resize" ? (drag.handle === "nw" || drag.handle === "se" ? "nwse-resize" : "nesw-resize")
      : "crosshair"
    : hoverCursor;

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 1000 1000"
      className="absolute inset-0 h-full w-full"
      style={{ cursor: activeCursor }}
      preserveAspectRatio="none"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={() => setDrag(null)}
    >
      {/* Transparent background to capture mouse events in empty areas */}
      <rect x="0" y="0" width="1000" height="1000" fill="transparent" />

      {/* Active listing box */}
      {activeDisplay && (
        <g>
          <rect
            x={activeDisplay.xMin}
            y={activeDisplay.yMin}
            width={activeDisplay.xMax - activeDisplay.xMin}
            height={activeDisplay.yMax - activeDisplay.yMin}
            fill="#22c55e"
            fillOpacity={0.1}
            stroke="#22c55e"
            strokeWidth={selectedId === "active" ? 4 : 3}
            rx={8}
          />
          {!hideDeleteButton && (
            <DeleteButton
              cx={activeDisplay.xMax - 4}
              cy={activeDisplay.yMin + 4}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveCurrentBox();
              }}
            />
          )}
          {selectedId === "active" && (
            <ResizeHandles box={activeDisplay} color="#22c55e" />
          )}
        </g>
      )}

      {/* Sibling boxes */}
      {siblings.map((sibling) => {
        const bb = getDisplayBox(sibling.id);
        if (!bb) return null;
        const isSelected = selectedId === sibling.id;

        return (
          <g key={sibling.id} style={readOnlySiblings ? { pointerEvents: "none" } : undefined}>
            <rect
              x={bb.xMin}
              y={bb.yMin}
              width={bb.xMax - bb.xMin}
              height={bb.yMax - bb.yMin}
              fill={isSelected ? "#3b82f6" : "#6b7280"}
              fillOpacity={readOnlySiblings ? 0.08 : 0.15}
              stroke={isSelected ? "#3b82f6" : "#9ca3af"}
              strokeWidth={isSelected ? 3 : 2}
              strokeDasharray={readOnlySiblings ? "6 4" : "0"}
              rx={8}
            />
            <rect
              x={bb.xMin}
              y={Math.max(0, bb.yMin - 28)}
              width={Math.min(bb.xMax - bb.xMin, sibling.gameTitle.length * 9 + 20)}
              height={24}
              fill="#6b7280"
              rx={4}
            />
            <text
              x={bb.xMin + 6}
              y={Math.max(0, bb.yMin - 28) + 16}
              fill="white"
              fontSize={12}
              fontWeight="500"
            >
              {sibling.gameTitle}
            </text>
            {/* Delete button for sibling */}
            {!readOnlySiblings && (
              <DeleteButton
                cx={bb.xMax - 4}
                cy={bb.yMin + 4}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveSibling(sibling.id);
                }}
              />
            )}
            {isSelected && !readOnlySiblings && (
              <ResizeHandles box={bb} color="#3b82f6" />
            )}
          </g>
        );
      })}

      {/* Newly drawn box (pending creation) */}
      {newBox && (() => {
        const nb = getDisplayBox("new") ?? newBox;
        const isSelected = selectedId === "new";
        return (
          <g>
            <rect
              x={nb.xMin}
              y={nb.yMin}
              width={nb.xMax - nb.xMin}
              height={nb.yMax - nb.yMin}
              fill="#f59e0b"
              fillOpacity={0.15}
              stroke="#f59e0b"
              strokeWidth={isSelected ? 4 : 3}
              strokeDasharray="8 4"
              rx={8}
            />
            {isSelected && (
              <ResizeHandles box={nb} color="#f59e0b" />
            )}
          </g>
        );
      })()}

      {/* Drawing rectangle */}
      {drawRect && (drawRect.xMax - drawRect.xMin) > 5 && (drawRect.yMax - drawRect.yMin) > 5 && (
        <rect
          x={drawRect.xMin}
          y={drawRect.yMin}
          width={drawRect.xMax - drawRect.xMin}
          height={drawRect.yMax - drawRect.yMin}
          fill="#3b82f6"
          fillOpacity={0.15}
          stroke="#3b82f6"
          strokeWidth={3}
          strokeDasharray="8 4"
          rx={8}
        />
      )}
    </svg>
  );
}

function ResizeHandles({ box, color }: { box: BoundingBox; color: string }) {
  const handles = [
    { x: box.xMin, y: box.yMin },
    { x: box.xMax, y: box.yMin },
    { x: box.xMin, y: box.yMax },
    { x: box.xMax, y: box.yMax },
  ];

  return (
    <>
      {handles.map((h, i) => (
        <circle
          key={i}
          cx={h.x}
          cy={h.y}
          r={12}
          fill="white"
          stroke={color}
          strokeWidth={3}
        />
      ))}
    </>
  );
}

function DeleteButton({
  cx,
  cy,
  onClick,
}: {
  cx: number;
  cy: number;
  onClick: (e: React.MouseEvent) => void;
}) {
  return (
    <g
      className="cursor-pointer"
      onClick={onClick}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <circle cx={cx} cy={cy} r={16} fill="#ef4444" />
      <line x1={cx - 7} y1={cy - 7} x2={cx + 7} y2={cy + 7} stroke="white" strokeWidth={3} strokeLinecap="round" />
      <line x1={cx + 7} y1={cy - 7} x2={cx - 7} y2={cy + 7} stroke="white" strokeWidth={3} strokeLinecap="round" />
    </g>
  );
}
